import {
  type CtaHeaderProps,
  type FooterV4Props,
  type HeaderV4Props,
} from '@jouzen/ecom-components/layout';
import { usePathname } from 'next/navigation';

import useDiscountMessage from '@/app/components/PageLayout/_hooks/useDiscountMessage';
import useLanguageSwitcher from '@/app/hooks/useLanguageSwitcher';

import useEyebrowConfig from './useEyebrowConfig';
import useFooterProps from './useFooterProps';
import useHeaderProps from './useHeaderProps';
import usePageAnalytics from './usePageAnalytics';

interface PageLayoutProps {
  enableFadeAnimations: boolean;
  parentCtaHeaderProps?: CtaHeaderProps | undefined;
  parentFooterProps?: Partial<FooterV4Props> | undefined;
  parentHeaderProps?: Partial<HeaderV4Props> | undefined;
}

const usePageLayout = ({
  enableFadeAnimations = false,
  parentCtaHeaderProps,
  parentFooterProps,
  parentHeaderProps,
}: PageLayoutProps) => {
  const currentPath = usePathname();
  const { locale, onLocaleChange, onLanguageSwitcherClick } =
    useLanguageSwitcher();
  const { displayMessage } = useDiscountMessage({
    isAccessory: false,
  });

  const { eyebrowConfig, eyebrowMessageType } = useEyebrowConfig({
    hasDiscountMessage: Boolean(displayMessage),
  });

  const {
    onFooterLinkClick,
    onHamburgerClick,
    onMenuLinkClick,
    sendEmailSubmitAnalytics,
  } = usePageAnalytics();

  const headerProps = useHeaderProps({
    currentPath,
    enableFadeAnimations,
    locale,
    onHamburgerClick,
    onMenuLinkClick,
    parentHeaderProps,
  });

  const footerProps = useFooterProps({
    currentPath,
    locale,
    onFooterLinkClick,
    onLocaleChange,
    onLanguageSwitcherClick,
    parentFooterProps,
    sendEmailSubmitAnalytics,
  });

  // only pass ctaHeaderProps to layout if it is defined
  const ctaHeaderProps = parentCtaHeaderProps
    ? {
        ctaHeaderProps: {
          inverse: headerProps.inverse ?? false,
          ...parentCtaHeaderProps,
        },
      }
    : {};

  return {
    ctaHeaderProps,
    displayMessage,
    eyebrowConfig,
    eyebrowMessageType,
    footerProps,
    headerProps,
  };
};

export default usePageLayout;
